import { gql } from 'graphql-request'

// Types for user column preferences
export interface ColumnConfig {
  id: string
  name: string
  enabled: boolean
  position: number
  frozen?: boolean
}

export interface UserColumnPreferenceParams {
  userEmail: string
  table: string
  columnsConfig: ColumnConfig[]
  clientMutationId?: string
}

export interface UpdateUserColumnPreferenceParams {
  userPreferenceId: string
  table: string
  columnsConfig: ColumnConfig[]
  clientMutationId?: string
}

export const mutationCreateUserColumnPreference = (params: UserColumnPreferenceParams): string => {
  const { userEmail, table, columnsConfig, clientMutationId = '1' } = params

  // Format columns config for GraphQL
  const formattedColumnsConfig = columnsConfig
    .map(
      (column) => `{
    id: "${column.id}",
    name: "${column.name}",
    enabled: ${column.enabled},
    position: ${column.position}${
        column.frozen !== undefined ? `,\n    frozen: ${column.frozen}` : ''
      }
  }`
    )
    .join(',')

  return gql`
    mutation {
      createUserPreference(
        clientMutationId: "${clientMutationId}",
        preferenceData: {
          userEmail: "${userEmail}"
          preferenceType: TABLE_COLUMN_SETTINGS
          preferenceDetails:  {
            table: "${table}"
            columnsConfig: [${formattedColumnsConfig}]
          }
        }
      ) {
        clientMutationId
        userPreferenceConfigId
      }
    }
  `
}

export const mutationUpdateUserColumnPreference = (
  params: UpdateUserColumnPreferenceParams
): string => {
  const { userPreferenceId, columnsConfig, clientMutationId = '1' } = params

  // Format columns config for GraphQL
  const formattedColumnsConfig = columnsConfig
    .map(
      (column) => `{
    id: "${column.id}",
    name: "${column.name}",
    enabled: ${column.enabled},
    position: ${column.position}${
        column.frozen !== undefined ? `,\n    frozen: ${column.frozen}` : ''
      }
  }`
    )
    .join(',')

  return gql`
    mutation {
      updateUserPreference(
        clientMutationId: "${clientMutationId}",
        userPreferenceId: "${userPreferenceId}",
        preferenceData: {
          preferenceDetails: {
            columnsConfig: [${formattedColumnsConfig}]
          }
        }
      ) {
        clientMutationId
        userPreferenceConfigId
      }
    }
  `
}

export const queryUserColumnPreferences = (userEmail?: string, table?: string): string => {
  // Build filter array dynamically
  const filters: string[] = []

  if (userEmail) {
    filters.push(`{ key: USER_EMAIL, values: ["${userEmail}"] }`)
  }

  // Always filter by preference type
  filters.push(`{ key: PREFERENCE_TYPE, values: ["TABLE_COLUMN_SETTINGS"] }`)

  return gql`
    query {
      userPreference(
        table: "${table}"
        filter: [${filters.join(',\n          ')}]
      ) {
        edges {
          node {
            id
            userEmail
            preferenceType
            updateTimestamp
            preferenceDetails {
              table
              columnsConfig {
                id
                name
                enabled
                position
                frozen
              }
            }
          }
        }
      }
    }
  `
}

export const queryAllUserColumnPreferences = (table?: string): string => {
  return queryUserColumnPreferences(undefined, table)
}

export const mapQueryUserColumnPreferences = (raw: any) => {
  try {
    return (
      raw?.data?.userPreference?.edges?.map((edge: any) => ({
        id: edge.node.id,
        userEmail: edge.node.userEmail,
        preferenceType: edge.node.preferenceType,
        table: edge.node.preferenceDetails?.table,
        columnsConfig: edge.node.preferenceDetails?.columnsConfig || [],
        updateTimestamp: edge.node.updateTimestamp
      })) || []
    )
  } catch (error) {
    console.error('Error mapping user column preferences:', error)
    return []
  }
}
